import { AppType } from "client/Api/Apps/common";
import {
	ReactElement,
	RefObject,
	useCallback,
	useRef,
	useState
} from "react";
import { useRenderManager } from "../../useRenderManager";
import { OneByOneTileContent } from "../OneByOneTileContent";
import { TwoByTwoTileContent } from "../TwoByTwoTileContent";
import { useRegisterTileUsage } from "../useRegisterTileUsage";
import { navigateToEmbedApp } from "client_legacy/Apps/appNavigation";
import { EmbedLiveTileContent, EmbedTilePopoutableContent } from "./EmbedLiveTileContent";
import { Portal } from "client/Components";
import { TileRenderState } from "../../RenderState/common";
import {
	useBaseNavigation,
	useConditionalAccessNavigation,
	useMaintenanceWindowNavigation
} from "../useInvokeNavigation";
import { TileContextProvider } from "../../TileContextProvider";
import { Deferred, deferred } from "client/Misc/utils";
import { TimeZone } from "client/DateAndTime/timezones";
import { Coordinate } from "client/Misc/commonTypes";

export type EmbedAppTileContentProps = {
	renderState: TileRenderState<AppType.Embed>;
	onEdit: (() => void) | undefined;
	userTimezone: TimeZone;
	onOpenTileMenu: (coordinate: Coordinate) => void;
};

export const EmbedAppTileContent = (props: EmbedAppTileContentProps): ReactElement => {
	const {
		renderState,
		onEdit,
		userTimezone,
		onOpenTileMenu
	} = props;

	const [navigationElement, navigateHandler] = useEmbedAppTileNavigation(
		renderState,
		onEdit,
		userTimezone
	);

	const {
		dimension,
		data: { settings }
	} = renderState;

	const tileContent: ReactElement = useRenderManager(
		dimension,
		() => (
			<EmbedLiveTileContent
				settings={settings}
				onNavigate={navigateHandler}
				onOpenTileMenu={onOpenTileMenu}
			/>
		),
		[
			{ columnSpan: 1, rowSpan: 1 },
			() => (
				<OneByOneTileContent
					onClick={navigateHandler}
					onOpenTileMenu={onOpenTileMenu}
				/>
			)
		],
		[
			{ columnSpan: 2, rowSpan: 2 },
			() => (
				<TwoByTwoTileContent
					onClick={navigateHandler}
					onOpenTileMenu={onOpenTileMenu}
				/>
			)
		]
	);

	return (
		<>
			<TileContextProvider
				renderState={renderState}
			>
				{tileContent}
			</TileContextProvider>

			{navigationElement}
		</>
	);
};
EmbedAppTileContent.displayName = "EmbedAppTileContent";

export function useEmbedAppTileNavigation(
	renderStateIsh: Pick<TileRenderState<AppType.Embed>, "data" | "dimension" | "interactionState">,
	onEdit: (() => void) | undefined,
	timezone: TimeZone
): [element: ReactElement | null, () => void | Promise<void>] {
	const [shouldShowDialog, setShouldShowDialog] = useState<boolean>(false);
	const pendingDialogCompletion: RefObject<Deferred<void> | null> = useRef<Deferred<void>>(null);

	const registerTileUsage: (() => void) = useRegisterTileUsage(renderStateIsh);

	const closeDialogHandler = useCallback(
		(): void => {
			pendingDialogCompletion.current
				?.resolve();

			pendingDialogCompletion.current = null;
			setShouldShowDialog(false);
		},
		[]
	);

	const {
		dimension: {
			dimension: { columnSpan }
		},
		data: {
			name,
			environmentAppId,
			conditionalAccess,
			maintenanceWindow
		}
	} = renderStateIsh;

	const [
		unavailableNavigationElement,
		navigationHandler
	] = useConditionalAccessNavigation(
		conditionalAccess,
		useMaintenanceWindowNavigation(
			maintenanceWindow,
			timezone,
			useCallback(
				async (): Promise<void> => {
					registerTileUsage();

					if (columnSpan <= 2) {
						pendingDialogCompletion.current
							?.resolve();

						const completion: Deferred<void> = deferred<void>();
						pendingDialogCompletion.current = completion;

						setShouldShowDialog(true);
						await completion.promise;
					} else {
						navigateToEmbedApp({
							id: environmentAppId
						});
					}
				},
				[
					columnSpan,
					environmentAppId,
					registerTileUsage
				]
			)
		)
	);

	return useBaseNavigation(
		renderStateIsh,
		onEdit,
		[
			unavailableNavigationElement
				?? (
					shouldShowDialog
						? (
							<Portal container={document.body}>
								<EmbedTilePopoutableContent
									environmentAppId={environmentAppId}
									name={name}
									onClose={closeDialogHandler}
								/>
							</Portal>
						)
						: null
				),
			navigationHandler
		]
	);
}
